import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  AdditionalNotificationRecipientRequest,
  AdditionalNotificationRecipientResponse,
  createAdditionalNotificationRecipient,
  deleteAdditionalNotificationRecipient,
  getAllAdditionalNotificationRecipients,
  UpdateAdditionalNotificationRecipientRequest,
  updateAdditionalNotificationRecipient,
} from '@app/api/additional-notification-recipient.api'
import {
  createNotificationTemplate,
  deleteNotificationTemplate,
  getAllNotificationTemplates,
  NotificationTemplateRequest,
  NotificationTemplateResponse,
  updateNotificationTemplate,
} from '@app/api/notification-template.api'
import {
  createNotification,
  createSenderAddress,
  deleteNotification,
  deleteSenderAddress,
  getAllNotifications,
  getAllSenderAddresses,
  GetAllSenderAddressesResponse,
  getSenderAddressDetails,
  GetSenderAddressDetailsResponse,
  NotificationRequest,
  NotificationResponse,
  NotificationSenderAddressRequest,
  NotificationSenderAddressResponse,
  toggleNotificationActive,
  toggleSenderAddressActivation,
  UpdateNotificationRequest,
  updateNotification,
  UpdateNotificationSenderAddressRequest,
  updateSenderAddress,
} from '@app/api/notification.api'

import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

type NotificationState = {
  senderAddressesData: Record<string, GetAllSenderAddressesResponse>
  senderAddressesListStatus: Status
  senderAddressDetailsData: Record<string, NotificationSenderAddressResponse>
  senderAddressDetailsStatusById: Record<string, Status>
  createSenderAddressStatus: Status
  updateSenderAddressStatus: Status
  deleteSenderAddressStatus: Status
  toggleSenderAddressStatus: Status
  additionalRecipientsData: Record<string, AdditionalNotificationRecipientResponse>
  additionalRecipientsListStatus: Status
  createAdditionalRecipientStatus: Status
  updateAdditionalRecipientStatus: Status
  deleteAdditionalRecipientStatus: Status
  notificationTemplatesData: Record<string, NotificationTemplateResponse>
  notificationTemplatesListStatus: Status
  createNotificationTemplateStatus: Status
  updateNotificationTemplateStatus: Status
  deleteNotificationTemplateStatus: Status
  notificationsData: Record<string, NotificationResponse>
  notificationsListStatus: Status
  createNotificationStatus: Status
  updateNotificationStatus: Status
  deleteNotificationStatus: Status
  toggleNotificationStatus: Status
}

const initialState: NotificationState = {
  senderAddressesData: {},
  senderAddressesListStatus: 'idle',
  senderAddressDetailsData: {},
  senderAddressDetailsStatusById: {},
  createSenderAddressStatus: 'idle',
  updateSenderAddressStatus: 'idle',
  deleteSenderAddressStatus: 'idle',
  toggleSenderAddressStatus: 'idle',
  additionalRecipientsData: {},
  additionalRecipientsListStatus: 'idle',
  createAdditionalRecipientStatus: 'idle',
  updateAdditionalRecipientStatus: 'idle',
  deleteAdditionalRecipientStatus: 'idle',
  notificationTemplatesData: {},
  notificationTemplatesListStatus: 'idle',
  createNotificationTemplateStatus: 'idle',
  updateNotificationTemplateStatus: 'idle',
  deleteNotificationTemplateStatus: 'idle',
  notificationsData: {},
  notificationsListStatus: 'idle',
  createNotificationStatus: 'idle',
  updateNotificationStatus: 'idle',
  deleteNotificationStatus: 'idle',
  toggleNotificationStatus: 'idle',
}

export const getAllSenderAddressesData = createAsyncThunk<GetAllSenderAddressesResponse[]>(
  'notification/getAllSenderAddressesData',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllSenderAddresses()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getSenderAddressDetailsData = createAsyncThunk<GetSenderAddressDetailsResponse, string>(
  'notification/getSenderAddressDetailsData',
  async (id, { rejectWithValue }) => {
    try {
      return await getSenderAddressDetails(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createSenderAddressData = createAsyncThunk<string, NotificationSenderAddressRequest>(
  'notification/createSenderAddressData',
  async (payload, { rejectWithValue }) => {
    try {
      return await createSenderAddress(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateSenderAddressData = createAsyncThunk<void, UpdateNotificationSenderAddressRequest>(
  'notification/updateSenderAddressData',
  async (payload, { rejectWithValue }) => {
    try {
      await updateSenderAddress(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteSenderAddressData = createAsyncThunk<void, string>(
  'notification/deleteSenderAddressData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteSenderAddress(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const toggleSenderAddressActivationData = createAsyncThunk<void, { id: string; isActive: boolean }>(
  'notification/toggleSenderAddressActivationData',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      await toggleSenderAddressActivation(id, { isActive })
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllAdditionalNotificationRecipientsData = createAsyncThunk<AdditionalNotificationRecipientResponse[]>(
  'notification/getAllAdditionalNotificationRecipientsData',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllAdditionalNotificationRecipients()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createAdditionalNotificationRecipientData = createAsyncThunk<
  string,
  AdditionalNotificationRecipientRequest
>('notification/createAdditionalNotificationRecipientData', async (payload, { rejectWithValue }) => {
  try {
    return await createAdditionalNotificationRecipient(payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const updateAdditionalNotificationRecipientData = createAsyncThunk<
  void,
  UpdateAdditionalNotificationRecipientRequest
>('notification/updateAdditionalNotificationRecipientData', async (payload, { rejectWithValue }) => {
  try {
    await updateAdditionalNotificationRecipient(payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const deleteAdditionalNotificationRecipientData = createAsyncThunk<void, string>(
  'notification/deleteAdditionalNotificationRecipientData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteAdditionalNotificationRecipient(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllNotificationTemplatesData = createAsyncThunk<NotificationTemplateResponse[]>(
  'notification/getAllNotificationTemplatesData',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllNotificationTemplates()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createNotificationTemplateData = createAsyncThunk<string, NotificationTemplateRequest>(
  'notification/createNotificationTemplateData',
  async (payload, { rejectWithValue }) => {
    try {
      return await createNotificationTemplate(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateNotificationTemplateData = createAsyncThunk<
  void,
  { id: string; data: Partial<NotificationTemplateRequest> }
>('notification/updateNotificationTemplateData', async ({ id, data }, { rejectWithValue }) => {
  try {
    await updateNotificationTemplate(id, data)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const deleteNotificationTemplateData = createAsyncThunk<void, string>(
  'notification/deleteNotificationTemplateData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteNotificationTemplate(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllNotificationsData = createAsyncThunk<NotificationResponse[]>(
  'notification/getAllNotificationsData',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllNotifications()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createNotificationData = createAsyncThunk<NotificationResponse, NotificationRequest>(
  'notification/createNotificationData',
  async (payload, { rejectWithValue }) => {
    try {
      return await createNotification(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateNotificationData = createAsyncThunk<
  NotificationResponse,
  { id: string; data: UpdateNotificationRequest }
>('notification/updateNotificationData', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await updateNotification(id, data)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const deleteNotificationData = createAsyncThunk<void, string>(
  'notification/deleteNotificationData',
  async (id, { rejectWithValue }) => {
    try {
      await deleteNotification(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const toggleNotificationActiveData = createAsyncThunk<
  NotificationResponse,
  { id: string; isActive: boolean; performedById?: string }
>('notification/toggleNotificationActiveData', async ({ id, isActive, performedById }, { rejectWithValue }) => {
  try {
    return await toggleNotificationActive(id, { isActive, performedById })
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllSenderAddressesData.pending, (state) => {
        state.senderAddressesListStatus = 'loading'
      })
      .addCase(getAllSenderAddressesData.rejected, (state) => {
        state.senderAddressesListStatus = 'failed'
      })
      .addCase(getAllSenderAddressesData.fulfilled, (state, action) => {
        state.senderAddressesData = Object.fromEntries(action.payload.map((sender) => [sender.id, sender]))
        state.senderAddressesListStatus = 'succeeded'
      })
      .addCase(getSenderAddressDetailsData.pending, (state, action) => {
        state.senderAddressDetailsStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getSenderAddressDetailsData.rejected, (state, action) => {
        state.senderAddressDetailsStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getSenderAddressDetailsData.fulfilled, (state, action) => {
        const id = action.meta.arg
        const listRecord = state.senderAddressesData[id]

        if (listRecord) state.senderAddressDetailsData[id] = { ...listRecord, ...action.payload }
        state.senderAddressDetailsStatusById[id] = 'succeeded'
      })
      .addCase(createSenderAddressData.pending, (state) => {
        state.createSenderAddressStatus = 'loading'
      })
      .addCase(createSenderAddressData.rejected, (state) => {
        state.createSenderAddressStatus = 'failed'
      })
      .addCase(createSenderAddressData.fulfilled, (state, action) => {
        const sender = action.meta.arg
        state.senderAddressesData[action.payload] = {
          id: action.payload,
          name: sender.name,
          email: sender.email,
          isActive: false,
        }
        state.senderAddressDetailsData[action.payload] = {
          id: action.payload,
          name: sender.name,
          description: sender.description ?? '',
          server: sender.server,
          port: sender.port,
          email: sender.email,
          isActive: false,
        }
        state.createSenderAddressStatus = 'succeeded'
      })
      .addCase(updateSenderAddressData.pending, (state) => {
        state.updateSenderAddressStatus = 'loading'
      })
      .addCase(updateSenderAddressData.rejected, (state) => {
        state.updateSenderAddressStatus = 'failed'
      })
      .addCase(updateSenderAddressData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        const listRecord = state.senderAddressesData[id]
        const detailsRecord = state.senderAddressDetailsData[id]

        if (listRecord) state.senderAddressesData[id] = { ...listRecord, name: data.name, email: data.email }
        if (detailsRecord) {
          state.senderAddressDetailsData[id] = {
            ...detailsRecord,
            name: data.name,
            description: data.description,
            email: data.email,
            port: data.port,
            server: data.server,
          }
        }
        state.updateSenderAddressStatus = 'succeeded'
      })
      .addCase(deleteSenderAddressData.pending, (state) => {
        state.deleteSenderAddressStatus = 'loading'
      })
      .addCase(deleteSenderAddressData.rejected, (state) => {
        state.deleteSenderAddressStatus = 'failed'
      })
      .addCase(deleteSenderAddressData.fulfilled, (state, action) => {
        delete state.senderAddressesData[action.meta.arg]
        delete state.senderAddressDetailsData[action.meta.arg]
        delete state.senderAddressDetailsStatusById[action.meta.arg]
        state.deleteSenderAddressStatus = 'succeeded'
      })
      .addCase(toggleSenderAddressActivationData.pending, (state) => {
        state.toggleSenderAddressStatus = 'loading'
      })
      .addCase(toggleSenderAddressActivationData.rejected, (state) => {
        state.toggleSenderAddressStatus = 'failed'
      })
      .addCase(toggleSenderAddressActivationData.fulfilled, (state, action) => {
        const { id, isActive } = action.meta.arg
        Object.values(state.senderAddressesData).forEach((sender) => {
          sender.isActive = sender.id === id ? isActive : isActive ? false : sender.isActive
        })
        Object.values(state.senderAddressDetailsData).forEach((sender) => {
          sender.isActive = sender.id === id ? isActive : isActive ? false : sender.isActive
        })
        state.toggleSenderAddressStatus = 'succeeded'
      })
      .addCase(getAllAdditionalNotificationRecipientsData.pending, (state) => {
        state.additionalRecipientsListStatus = 'loading'
      })
      .addCase(getAllAdditionalNotificationRecipientsData.rejected, (state) => {
        state.additionalRecipientsListStatus = 'failed'
      })
      .addCase(getAllAdditionalNotificationRecipientsData.fulfilled, (state, action) => {
        state.additionalRecipientsData = Object.fromEntries(
          action.payload.map((recipient) => [recipient.id, recipient]),
        )
        state.additionalRecipientsListStatus = 'succeeded'
      })
      .addCase(createAdditionalNotificationRecipientData.pending, (state) => {
        state.createAdditionalRecipientStatus = 'loading'
      })
      .addCase(createAdditionalNotificationRecipientData.rejected, (state) => {
        state.createAdditionalRecipientStatus = 'failed'
      })
      .addCase(createAdditionalNotificationRecipientData.fulfilled, (state, action) => {
        state.additionalRecipientsData[action.payload] = { id: action.payload, ...action.meta.arg }
        state.createAdditionalRecipientStatus = 'succeeded'
      })
      .addCase(updateAdditionalNotificationRecipientData.pending, (state) => {
        state.updateAdditionalRecipientStatus = 'loading'
      })
      .addCase(updateAdditionalNotificationRecipientData.rejected, (state) => {
        state.updateAdditionalRecipientStatus = 'failed'
      })
      .addCase(updateAdditionalNotificationRecipientData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        state.additionalRecipientsData[id] = { ...state.additionalRecipientsData[id], ...data, id }
        state.updateAdditionalRecipientStatus = 'succeeded'
      })
      .addCase(deleteAdditionalNotificationRecipientData.pending, (state) => {
        state.deleteAdditionalRecipientStatus = 'loading'
      })
      .addCase(deleteAdditionalNotificationRecipientData.rejected, (state) => {
        state.deleteAdditionalRecipientStatus = 'failed'
      })
      .addCase(deleteAdditionalNotificationRecipientData.fulfilled, (state, action) => {
        delete state.additionalRecipientsData[action.meta.arg]
        state.deleteAdditionalRecipientStatus = 'succeeded'
      })
      .addCase(getAllNotificationTemplatesData.pending, (state) => {
        state.notificationTemplatesListStatus = 'loading'
      })
      .addCase(getAllNotificationTemplatesData.rejected, (state) => {
        state.notificationTemplatesListStatus = 'failed'
      })
      .addCase(getAllNotificationTemplatesData.fulfilled, (state, action) => {
        state.notificationTemplatesData = Object.fromEntries(action.payload.map((template) => [template.id, template]))
        state.notificationTemplatesListStatus = 'succeeded'
      })
      .addCase(createNotificationTemplateData.pending, (state) => {
        state.createNotificationTemplateStatus = 'loading'
      })
      .addCase(createNotificationTemplateData.rejected, (state) => {
        state.createNotificationTemplateStatus = 'failed'
      })
      .addCase(createNotificationTemplateData.fulfilled, (state, action) => {
        const now = new Date().toISOString()
        state.notificationTemplatesData[action.payload] = {
          id: action.payload,
          ...action.meta.arg,
          createdAt: now,
          updatedAt: now,
        }
        state.createNotificationTemplateStatus = 'succeeded'
      })
      .addCase(updateNotificationTemplateData.pending, (state) => {
        state.updateNotificationTemplateStatus = 'loading'
      })
      .addCase(updateNotificationTemplateData.rejected, (state) => {
        state.updateNotificationTemplateStatus = 'failed'
      })
      .addCase(updateNotificationTemplateData.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg
        state.notificationTemplatesData[id] = {
          ...state.notificationTemplatesData[id],
          ...data,
          updatedAt: new Date().toISOString(),
        }
        state.updateNotificationTemplateStatus = 'succeeded'
      })
      .addCase(deleteNotificationTemplateData.pending, (state) => {
        state.deleteNotificationTemplateStatus = 'loading'
      })
      .addCase(deleteNotificationTemplateData.rejected, (state) => {
        state.deleteNotificationTemplateStatus = 'failed'
      })
      .addCase(deleteNotificationTemplateData.fulfilled, (state, action) => {
        delete state.notificationTemplatesData[action.meta.arg]
        state.deleteNotificationTemplateStatus = 'succeeded'
      })
      .addCase(getAllNotificationsData.pending, (state) => {
        state.notificationsListStatus = 'loading'
      })
      .addCase(getAllNotificationsData.rejected, (state) => {
        state.notificationsListStatus = 'failed'
      })
      .addCase(getAllNotificationsData.fulfilled, (state, action) => {
        state.notificationsData = Object.fromEntries(
          action.payload.map((notification) => [notification.id, notification]),
        )
        state.notificationsListStatus = 'succeeded'
      })
      .addCase(createNotificationData.pending, (state) => {
        state.createNotificationStatus = 'loading'
      })
      .addCase(createNotificationData.rejected, (state) => {
        state.createNotificationStatus = 'failed'
      })
      .addCase(createNotificationData.fulfilled, (state, action) => {
        state.notificationsData[action.payload.id] = action.payload
        state.createNotificationStatus = 'succeeded'
      })
      .addCase(updateNotificationData.pending, (state) => {
        state.updateNotificationStatus = 'loading'
      })
      .addCase(updateNotificationData.rejected, (state) => {
        state.updateNotificationStatus = 'failed'
      })
      .addCase(updateNotificationData.fulfilled, (state, action) => {
        state.notificationsData[action.payload.id] = action.payload
        state.updateNotificationStatus = 'succeeded'
      })
      .addCase(deleteNotificationData.pending, (state) => {
        state.deleteNotificationStatus = 'loading'
      })
      .addCase(deleteNotificationData.rejected, (state) => {
        state.deleteNotificationStatus = 'failed'
      })
      .addCase(deleteNotificationData.fulfilled, (state, action) => {
        delete state.notificationsData[action.meta.arg]
        state.deleteNotificationStatus = 'succeeded'
      })
      .addCase(toggleNotificationActiveData.pending, (state) => {
        state.toggleNotificationStatus = 'loading'
      })
      .addCase(toggleNotificationActiveData.rejected, (state) => {
        state.toggleNotificationStatus = 'failed'
      })
      .addCase(toggleNotificationActiveData.fulfilled, (state, action) => {
        state.notificationsData[action.payload.id] = action.payload
        state.toggleNotificationStatus = 'succeeded'
      })
  },
})

export default notificationSlice.reducer
